import {Article, Attribution, AutoFixHigh, Save, Visibility, VisibilityOff} from '@mui/icons-material';
import {Box, Button, FormControlLabel, Grid, Paper, Stack, Switch, Typography} from '@mui/material';
import {StudyContent, StudyStatus} from '@cp/model';
import DropdownStudyStatus from '../../../atoms/dropdownStudyStatus/DropdownStudyStatus';
import {env} from '../../../config/env';
import usePagination from '../../../hooks/helper/usePagination';
import AdminStudyEditorContent from './AdminStudyEditorContent';
import AdminStudyEditorPreview from './AdminStudyEditorPreview';
import AdminStudyEditorStudyContent from './AdminStudyEditorStudyContent';
import mapStudyContentToContent from './utils/mapStudyContentToContent';
import formatPlotlyJsonInMarkdown from './utils/formatPlotlyJsonInMarkdown';

export interface AdminStudyEditorProps {
  content: string;
  setContent: (content: string) => void;
  studyContent: StudyContent[];
  setStudyContent: (studyContent: StudyContent[]) => void;
  pageMode: boolean;
  setPageMode: (pageMode: boolean) => void;
  status: StudyStatus | undefined;
  onStatusChange: (status: StudyStatus) => void;
  showPreview: boolean;
  onTogglePreview: () => void;
  studyLoading: boolean;
  saving: boolean;
  canViewStudy: boolean;
  onViewStudy: () => void;
  onSave: () => void;
  moduleId: string;
  topic: string;
}

export default function AdminStudyEditor({
  content,
  studyContent,
  setContent,
  setStudyContent,
  pageMode,
  setPageMode,
  status,
  showPreview,
  studyLoading,
  saving,
  canViewStudy,
  onStatusChange,
  onTogglePreview,
  onViewStudy,
  onSave,
  moduleId,
  topic,
}: AdminStudyEditorProps) {
  // Use pagination with pageSize=1 (one section per page)
  // Convert between 1-based page number and 0-based section index
  const {startIndex, nextPage, prevPage, gotoPage, isFirstPage, isLastPage} = usePagination({
    pageSize: 1,
    totalItems: pageMode ? studyContent.length : 0,
    initialPage: 1,
  });

  // Handle content change from page mode (sections)
  const handleSectionsContentChange = (_index: number, updatedSections: typeof studyContent) => {
    setStudyContent(updatedSections);
  };

  // Handle format plotly button click
  const handleFormatPlotly = () => {
    if (pageMode) {
      // Format current section only
      if (studyContent[startIndex]) {
        const formattedContent = formatPlotlyJsonInMarkdown(studyContent[startIndex].content);
        const updatedSections = [...studyContent];
        updatedSections[startIndex] = {
          ...updatedSections[startIndex],
          content: formattedContent,
        };
        setStudyContent(updatedSections);
      }
    } else {
      // Format whole article
      const formattedContent = formatPlotlyJsonInMarkdown(content);
      setContent(formattedContent);
    }
  };

  // Handle add credit button click
  const handleAddCredit = async () => {
    const creditText = `> This embedded video is sourced directly from YouTube and remains the intellectual property of its original creator.
    We do not claim ownership of this content. All rights and credit are attributed to the original YouTube channel.
    Please click on the video if you want more similiar content from the creator on youtube.`;
    try {
      await navigator.clipboard.writeText(creditText);
    } catch (err) {
      console.error('Failed to copy credit text:', err);
    }
  };

  return (
    <Paper sx={{p: 3}}>
      <Stack spacing={2}>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2}}>
          <Typography variant="h6" sx={{fontWeight: 600}}>
            Content (Markdown)
          </Typography>
          <Box sx={{display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap'}}>
            <FormControlLabel
              control={<Switch checked={pageMode} onChange={(e) => setPageMode(e.target.checked)} size="small" />}
              label="Page Mode"
              sx={{m: 0}}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoFixHigh />}
              onClick={handleFormatPlotly}
              disabled={saving || studyLoading || (pageMode && studyContent.length === 0)}
            >
              Format Plotly
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Attribution />}
              onClick={handleAddCredit}
              disabled={saving || studyLoading}
            >
              Add Credit
            </Button>
            <DropdownStudyStatus
              status={status}
              onChange={(newStatus) => {
                if (newStatus) {
                  onStatusChange(newStatus);
                }
              }}
              availableStatuses={[StudyStatus.GENERATED, StudyStatus.REVIEWED, StudyStatus.FLAGGED]}
              disabled={saving || studyLoading}
              fullWidth={false}
              minWidth={150}
              size="small"
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={showPreview ? <VisibilityOff /> : <Visibility />}
              onClick={onTogglePreview}
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Save />}
              onClick={onSave}
              disabled={saving || studyLoading}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Article />}
              onClick={onViewStudy}
              disabled={!canViewStudy}
            >
              View Study
            </Button>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Use markdown for formatting. Use {env.study.separator} as start of a session. Use $...$ for inline math and
          ```katex...``` for block math.
        </Typography>
        {studyLoading ? (
          <Box sx={{textAlign: 'center', py: 4}}>
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{alignItems: 'stretch'}}>
            <Grid
              size={showPreview ? {xs: 12, md: 6} : {xs: 12}}
              sx={{display: 'flex', flexDirection: 'column', minHeight: 0}}
            >
              {pageMode ? (
                <AdminStudyEditorStudyContent
                  sections={studyContent}
                  currentSectionIndex={startIndex}
                  onContentChange={handleSectionsContentChange}
                  onPrevious={prevPage}
                  onNext={nextPage}
                  onJumpToSection={gotoPage}
                  isFirstPage={isFirstPage}
                  isLastPage={isLastPage}
                  moduleId={moduleId}
                  topic={topic}
                />
              ) : (
                <AdminStudyEditorContent
                  content={content}
                  onContentChange={setContent}
                  moduleId={moduleId}
                  topic={topic}
                />
              )}
            </Grid>
            {showPreview && (
              <Grid size={{xs: 12, md: 6}}>
                <AdminStudyEditorPreview
                  content={
                    pageMode && studyContent[startIndex] ? mapStudyContentToContent(studyContent[startIndex]) : content
                  }
                />
              </Grid>
            )}
          </Grid>
        )}
      </Stack>
    </Paper>
  );
}
